<template>
  <SimpleTable
    card-wrap
    class="balance-due-card full-height"
    :class="{ owing }"
    data-cy="balance-due-card"
  >
    <thead>
      <tr>
        <th :class="{ 'text-amber-800': owing }">
          <i
            :class="[
              'fa-regular mr-2',
              owing ? 'fa-triangle-exclamation' : 'fa-circle-check'
            ]"
          ></i>
          <span
            class="info-header text-sm font-bold"
            data-cy="balance-due-header"
          >
            {{ header }}
          </span>
        </th>
      </tr>
    </thead>
    <tr class="list-item">
      <td>
        <div
          class="font-bold"
          :class="owing ? 'text-xl text-amber-800' : 'text-base text-gray-500'"
          data-cy="balance-due-amount"
        >
          {{ dollars(balanceDue.cents) }}
        </div>
        <div
          class="text-xs mb-2"
          :class="owing ? 'text-amber-800' : 'text-gray-500'"
          data-cy="balance-due-summary"
        >
          {{ summary }}
        </div>
        <Button
          v-if="owing"
          type="Secondary"
          size="extra-small"
          outline
          label="View invoices ›"
          data-cy="balance-due-view-invoices"
          @click="viewUnpaidInvoices"
        />
      </td>
    </tr>
  </SimpleTable>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { EventBus } from "@/lib/event_bus";
import { updateQueryParam } from "@/lib/url";

import Button from "@/components/Seranova/Buttons/Button.vue";
import SimpleTable from "@/components/SimpleTable.vue";
import { dollars, pluralize } from "@/mixins";
import { BalanceDue } from "@/typings/customer";

// Must match the id on CustomerInvoicesDatatable; DatatableV3 scopes its
// filter query params as `${id}_${filterName}`.
const INVOICES_TABLE_ID = "invoices-table";

const props = defineProps<{ balanceDue: BalanceDue }>();

const owing = computed(() => props.balanceDue.cents > 0);

const header = computed(() => {
  if (owing.value) return "Balance Due";
  return props.balanceDue.cents < 0 ? "Credit Balance" : "No Balance";
});

// invoice_count covers both unpaid and credit invoices, so the line stays neutral.
const summary = computed(() => {
  if (props.balanceDue.cents === 0) return "Paid in full";

  const count = props.balanceDue.invoice_count;
  return `Across ${count} ${pluralize(count, "invoice")}`;
});

// The total excludes sub-customers and drafts; clear those table filters so the list matches.
async function viewUnpaidInvoices() {
  await updateQueryParam(`${INVOICES_TABLE_ID}_customer_id`, null, true);
  await updateQueryParam(`${INVOICES_TABLE_ID}_include_drafts`, null, true);
  await updateQueryParam(`${INVOICES_TABLE_ID}_paid`, "false");
  EventBus.$emit("set-selected-tab", "Invoices");
  EventBus.$emit("remount-invoice-list");
}
</script>

<style lang="scss" scoped>
.balance-due-card.owing {
  background-color: theme("colors.amber.50");
  border-color: theme("colors.amber.400");
  border-left-width: 4px;
}
</style>
